import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/settings")({
  component: InventorySettingsPage,
});

import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Loader2, Settings2, X } from "lucide-react";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/reusable-components/header/page-header";

import {
  useFetchInventorySettings,
  useUpdateInventorySettings,
} from "@/hooks/api/inventory/useInventorySettings";
import {
  CURRENCY_OPTIONS,
  INVENTORY_SETTINGS_FIELDS,
  inventorySettingsSchema,
  type InventorySettingsFormValues,
} from "@/schemas/inventory/inventory-settings.schema";
import { applyServerErrors } from "@/lib/apply-server-errors";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

const DEFAULTS: InventorySettingsFormValues = {
  default_inventory_currency: "USD",
  auto_receive_po_on_bill_received: false,
  allow_direct_bill_creation: false,
  return_inspection_enabled: true,
  calibration_check_enabled: true,
  lost_missing_enabled: true,
};

function InventorySettingsPage() {
  const settingsQuery = useFetchInventorySettings();
  const settings = settingsQuery.data ?? null;
  const fetchLoading = settingsQuery.isPending;

  const updateSettings = useUpdateInventorySettings();
  const [isEditing, setIsEditing] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);
  const { data: capabilities } = useMyPermissions();
  const canManageSettings = can(capabilities, "inventory.setting.manage");

  const {
    handleSubmit,
    reset,
    control,
    setError,
    formState: { errors },
  } = useForm<InventorySettingsFormValues>({
    resolver: zodResolver(inventorySettingsSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (settings) {
      reset({
        default_inventory_currency: settings.default_inventory_currency,
        auto_receive_po_on_bill_received:
          settings.auto_receive_po_on_bill_received,
        allow_direct_bill_creation: settings.allow_direct_bill_creation,
        return_inspection_enabled: settings.return_inspection_enabled,
        calibration_check_enabled: settings.calibration_check_enabled,
        lost_missing_enabled: settings.lost_missing_enabled,
      });
      setHasHydrated(true);
    }
  }, [settings, reset]);

  const handleFormSubmit = async (data: InventorySettingsFormValues) => {
    try {
      await updateSettings.mutateAsync(data);
      toast.success("Inventory settings updated");
      setIsEditing(false);
    } catch (err) {
      const { handled, rest } = applyServerErrors(
        err,
        setError,
        INVENTORY_SETTINGS_FIELDS
      );
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to update inventory settings");
    }
  };

  const handleCancel = () => {
    if (settings) {
      reset({
        default_inventory_currency: settings.default_inventory_currency,
        auto_receive_po_on_bill_received:
          settings.auto_receive_po_on_bill_received,
        allow_direct_bill_creation: settings.allow_direct_bill_creation,
        return_inspection_enabled: settings.return_inspection_enabled,
        calibration_check_enabled: settings.calibration_check_enabled,
        lost_missing_enabled: settings.lost_missing_enabled,
      });
    }
    setIsEditing(false);
  };

  const isLoading = fetchLoading || updateSettings.isPending;

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Inventory Configuration"
          description="Org-wide defaults for pricing, purchasing, and returns."
          isLoading={isLoading}
        >
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit(handleFormSubmit)}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {updateSettings.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            canManageSettings && (
              <Button
                onClick={() => setIsEditing(true)}
                disabled={isLoading}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Settings2 className="h-4 w-4" />
                Edit Settings
              </Button>
            )
          )}
        </PageHeader>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Inventory Configurations
            </CardTitle>
            <CardDescription className="text-justify">
              These apply across all of Inventory for this organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {fetchLoading || !hasHydrated ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-10 w-full rounded" />
                <Skeleton className="h-10 w-full rounded" />
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                className="space-y-4"
              >
                {/* Default Currency Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="default_inventory_currency">
                      Default Currency
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      Used for an item&apos;s price when no price book covers
                      it. A price book always wins over this when it has a
                      matching price, and carries its own currency.
                    </p>
                  </div>
                  <div className="w-full sm:w-[200px] sm:shrink-0">
                    <Controller
                      name="default_inventory_currency"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        >
                          <SelectTrigger
                            id="default_inventory_currency"
                            className="w-full"
                          >
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCY_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label} ({opt.value})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.default_inventory_currency && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.default_inventory_currency.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Auto Receive PO Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto_receive_po_on_bill_received">
                      Auto-receive PO when bill is received
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      When a bill linked to a purchase order is marked received,
                      also receive the PO&apos;s outstanding bulk
                      (non-serialized) items automatically. Serialized items
                      always still need a manual receive, since a real asset
                      identifier can&apos;t be generated automatically. Also
                      editable from the Bills page.
                    </p>
                  </div>
                  <div className="flex w-full justify-start sm:w-auto sm:shrink-0">
                    <Controller
                      name="auto_receive_po_on_bill_received"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="auto_receive_po_on_bill_received"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Direct Bill Creation Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="allow_direct_bill_creation">
                      Direct Bill Creation
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      Allow creating a bill without a purchase order. When that
                      bill is marked received, stock increases the same way a
                      PO&apos;s receipt would, at the warehouse chosen on the
                      bill. Serialized items work the same as a PO too - their
                      asset identifiers are collected in a separate step when
                      the bill is marked received.
                    </p>
                  </div>
                  <div className="flex w-full justify-start sm:w-auto sm:shrink-0">
                    <Controller
                      name="allow_direct_bill_creation"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="allow_direct_bill_creation"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Lost/Missing Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="lost_missing_enabled">
                      Lost/Missing on Return
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      When on, a return can mark every unit not selected to
                      physically return as lost/missing -- written off and
                      never credited back to stock, instead of staying
                      outstanding for a later return. Off: the option is
                      hidden on the return form, and a submission that tries
                      to use it anyway is refused.
                    </p>
                  </div>
                  <div className="flex w-full justify-start sm:w-auto sm:shrink-0">
                    <Controller
                      name="lost_missing_enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="lost_missing_enabled"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>
                
                {/* Return Inspection Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="return_inspection_enabled">
                      Return Inspection
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      When off, an item returned from a rental or deployment
                      goes straight to Available instead of In Inspection. This
                      is only the default - each return can still switch it for
                      that one submission; to change the default itself, change
                      it here.
                    </p>
                  </div>
                  <div className="flex w-full justify-start sm:w-auto sm:shrink-0">
                    <Controller
                      name="return_inspection_enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="return_inspection_enabled"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Calibration Check Field */}
                <div className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="calibration_check_enabled">
                      Calibration Check
                    </Label>
                    <p className="text-xs text-muted-foreground text-justify">
                      When off, Return Inspection no longer shows or requires
                      the Calibration Valid check, and an out-of-tolerance
                      calibration can no longer force a unit to Not Safe -- the
                      outcome rests entirely on the checklist result.
                    </p>
                  </div>
                  <div className="flex w-full justify-start sm:w-auto sm:shrink-0">
                    <Controller
                      name="calibration_check_enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="calibration_check_enabled"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isEditing || isLoading}
                        />
                      )}
                    />
                  </div>
                </div>

              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </SidebarInset>
  );
}
